import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Calendar, 
  Globe,
  CheckCircle,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerConsentHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsentLog();
  }, []);

  const fetchConsentLog = async () => {
    const token = localStorage.getItem('partnerToken');
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/partner/consent-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch consent log:', error);
      toast.error('Failed to fetch consent history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' IST';
  };

  return (
    <div className="space-y-6" data-testid="partner-consent-history">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield className="w-7 h-7 text-amber-400" />
          Consent History
        </h1>
        <p className="text-slate-400 mt-1">
          View your consent records and timestamps
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-emerald-500/10 border-emerald-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div>
              <p className="text-emerald-400 font-medium">Your Consent Status</p>
              <p className="text-slate-400 text-sm mt-1">
                You have provided consent for data processing. Your consent is recorded with timestamp and IP address for compliance purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consent Log */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-amber-400" />
            Consent Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-400">Loading consent history...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No consent records found</p>
              <p className="text-slate-500 text-sm mt-1">
                Your consent history will appear here after you provide consent
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {logs.map((log, index) => (
                <div key={index} className="p-4 hover:bg-slate-800/30 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {log.action === 'consent_given' ? 'Consent Given' : log.action}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-slate-400">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          <span>{formatDate(log.consent_date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-400">
                          <Globe className="w-4 h-4 text-slate-500" />
                          <span>IP: <code className="text-amber-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">{log.ip_address}</code></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Privacy Note */}
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="p-4">
          <p className="text-slate-500 text-sm">
            <strong className="text-slate-400">Privacy Note:</strong> Your consent records are stored securely and include your IP address at the time of consent for legal compliance. 
            You can withdraw consent at any time by contacting support, though this may affect your ability to use certain services.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerConsentHistory;
